/**
 * Measures: one number per month, one place, one timestamp. Each measure is
 * defined in Admin (its unit, owner, where it comes from, the month it counts
 * from) and has a value for each month: typed in, or pulled from what the
 * system already holds when a month is captured. Every value is unconfirmed
 * until its owner or a director confirms it. Trends chart a measure from its
 * live-from month on, unconfirmed figures marked as such.
 *
 * A measure can have a target for each month; the value against it is on
 * track, at risk or behind, by thresholds in Admin. The owner can add the
 * month's "why"; the system never writes one.
 */
import { addDays, daysBetween, endOfMonth, type ISODate } from "./dates";
import { engineInput } from "./model";
import { forecast } from "./engine";
import { actionState, dueOf } from "./actions";
import { obligationNow } from "./obligations";
import { releaseTiming } from "./releases";
import { bandOf, score } from "./risks";
import type { FinanceModel } from "./types";
import type { RegistersModel } from "./registers";
import type { HubMeasures } from "@otii/kit/hub-api";

/** Sources the Sales Hub works out a month at a time, read when the month is captured. */
export const HUB_MEASURE_SOURCES = ["sites_live", "demo_conversion", "retention_12m"] as const satisfies readonly MeasureSource[];

export type MeasureUnit = "gbp" | "percent" | "number" | "months" | "days";
export const UNIT_LABEL: Record<MeasureUnit, string> = { gbp: "£", percent: "%", number: "Number", months: "Months", days: "Days" };

/** Where a measure's value comes from: typed in, or worked out from what the system holds. */
export type MeasureSource =
  | "entered"
  | "cash_month_end"
  | "gate_low"
  | "runway"
  | "billed_unpaid"
  | "mrr"
  | "customers"
  | "sites_billed"
  | "sites_live"
  | "demo_conversion"
  | "retention_12m"
  | "open_actions"
  | "overdue_actions"
  | "overdue_obligations"
  | "high_risks"
  | "changes_over_target";

export const SOURCE: Record<MeasureSource, { label: string; unit: MeasureUnit | null; from: string }> = {
  entered: { label: "Typed in", unit: null, from: "" },
  cash_month_end: { label: "Cash at month end", unit: "gbp", from: "Bank balance" },
  gate_low: { label: "13-week cash low", unit: "gbp", from: "Finance, approved forecast" },
  runway: { label: "Months to the first week overdrawn", unit: "months", from: "Finance, approved forecast" },
  billed_unpaid: { label: "Billed but unpaid", unit: "gbp", from: "Xero" },
  mrr: { label: "Monthly recurring revenue", unit: "gbp", from: "Sales Hub billing schedule" },
  customers: { label: "Paying customers", unit: "number", from: "Sales Hub billing schedule" },
  sites_billed: { label: "Sites billed", unit: "number", from: "Sales Hub billing schedule" },
  sites_live: { label: "Sites live", unit: "number", from: "Sales Hub onboarding" },
  demo_conversion: { label: "Demos converted", unit: "percent", from: "Sales Hub pipeline" },
  retention_12m: { label: "Customers kept over 12 months", unit: "percent", from: "Sales Hub customers" },
  open_actions: { label: "Open actions", unit: "number", from: "Actions" },
  overdue_actions: { label: "Overdue actions", unit: "number", from: "Actions" },
  overdue_obligations: { label: "Overdue obligations", unit: "number", from: "Obligations" },
  high_risks: { label: "Risks in the top two bands", unit: "number", from: "Risks" },
  changes_over_target: { label: "Statutory changes over target", unit: "number", from: "Release log" },
};

export interface MeasureDef {
  id: string;
  name: string;
  unit: MeasureUnit;
  /** One of the headline measures, or a supporting one. */
  headline: boolean;
  /** The pack section it's reported in. */
  section: string;
  ownerId: string | null;
  source: MeasureSource;
  /** "YYYY-MM": counts from this month; null while it can't be measured yet. */
  liveFrom: string | null;
  /** What stands in until it's live. */
  standIn: string;
  better: "higher" | "lower";
  active: boolean;
  sortOrder: number;
}

export interface MeasureValue {
  measureId: string;
  /** "YYYY-MM". */
  month: string;
  /** Pence for £; otherwise as shown (23.5 for 23.5%). */
  value: number;
  origin: "entered" | "pulled";
  /** Where a pulled value came from, as data ("Bank balance, 30 Sep 2026"). */
  detail: string;
  capturedBy: string | null;
  capturedAt: string;
  confirmedBy: string | null;
  confirmedAt: string | null;
  /** The owner's why for the month. */
  note: string;
  noteBy: string | null;
}

export interface MeasureTarget {
  measureId: string;
  month: string;
  value: number;
}

export interface MeasureSettings {
  /** Value ÷ target (target ÷ value when lower is better), in per cent: on track at or above this. */
  onTrackPct: number;
  /** At risk at or above this; behind below it. */
  atRiskPct: number;
}
export const DEFAULT_MEASURE_SETTINGS: MeasureSettings = { onTrackPct: 95, atRiskPct: 80 };

export type MeasureStatus = "on_track" | "at_risk" | "behind";
export const MEASURE_STATUS_LABEL: Record<MeasureStatus, string> = { on_track: "On track", at_risk: "At risk", behind: "Behind" };

export const monthOf = (d: ISODate) => d.slice(0, 7);
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Sep 2026". */
export const monthName = (month: string) => `${MONTH_SHORT[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
export const prevMonth = (month: string) => monthOf(addDays(`${month}-01`, -1));
export const nextMonth = (month: string) => monthOf(addDays(monthEnd(month), 1));
export const monthEnd = (month: string) => endOfMonth(`${month}-01`);
export const isLive = (m: Pick<MeasureDef, "liveFrom">, month: string) => !!m.liveFrom && month >= m.liveFrom;

/** How far a figure is toward its target, in per cent (100 = met; turned round when lower is better). */
export function pctOfTarget(value: number, target: number, better: MeasureDef["better"]): number {
  let ratio: number;
  if (better === "higher") ratio = target === 0 ? (value >= 0 ? 1 : 0) : value / target;
  else ratio = value <= target ? 1 : value === 0 ? 1 : target / value;
  return ratio * 100;
}

/** Against its target: on track, at risk or behind. Null without a target. */
export function statusOf(value: number, target: number | null | undefined, better: MeasureDef["better"], s: MeasureSettings): MeasureStatus | null {
  if (target === null || target === undefined) return null;
  const pct = pctOfTarget(value, target, better);
  return pct >= s.onTrackPct - 1e-9 ? "on_track" : pct >= s.atRiskPct - 1e-9 ? "at_risk" : "behind";
}

/** "£12,400", "23.5%", "4.2 months". */
export function formatMeasure(value: number | null | undefined, unit: MeasureUnit): string {
  if (value === null || value === undefined) return "—";
  if (unit === "gbp") return `${value < 0 ? "−" : ""}£${Math.round(Math.abs(value) / 100).toLocaleString("en-GB")}`;
  if (unit === "percent") return `${Math.round(value * 10) / 10}%`;
  if (unit === "months") return `${Math.round(value * 10) / 10} ${value === 1 ? "month" : "months"}`;
  if (unit === "days") return `${Math.round(value)} ${Math.round(value) === 1 ? "day" : "days"}`;
  return Number.isInteger(value) ? value.toLocaleString("en-GB") : (Math.round(value * 10) / 10).toLocaleString("en-GB");
}

/** Short figures for an axis: "£50k", "£950", "30%", "21". */
export function axisMeasure(v: number, unit: MeasureUnit): string {
  const r = (n: number) => Math.round(n * 10) / 10;
  if (unit === "gbp") {
    const a = Math.abs(v) / 100;
    const s = a >= 1000 ? `£${r(a / 1000)}k` : `£${Math.round(a)}`;
    return v < 0 ? `−${s}` : s;
  }
  if (unit === "percent") return `${r(v)}%`;
  return `${v < 0 ? "−" : ""}${r(Math.abs(v)).toLocaleString("en-GB")}`;
}

/** Typed-in figures are in pounds for £; stored in pence. */
export const toStored = (shown: number, unit: MeasureUnit) => (unit === "gbp" ? Math.round(shown * 100) : shown);
export const toShown = (stored: number, unit: MeasureUnit) => (unit === "gbp" ? stored / 100 : stored);

/* ------------------------------------------------------------------ */
/* Pulling a month's figure                                            */
/* ------------------------------------------------------------------ */

export interface Pulled {
  value: number;
  detail: string;
}

const dayText = (d: ISODate) => {
  const [y, m, dd] = d.split("-").map(Number) as [number, number, number];
  return `${dd} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]} ${y}`;
};

/** The Sales Hub's customers paying at a month end: each one's latest invoice within its billing period. */
function payingAt(model: FinanceModel, end: ISODate) {
  const latest = new Map<string, FinanceModel["synced"]["hubBilling"][number]>();
  for (const b of model.synced.hubBilling) {
    if (b.date > end) continue;
    const reach = b.schedule === "annual" ? 365 : 92;
    if (daysBetween(b.date, end) >= reach) continue;
    const was = latest.get(b.organisationId);
    if (!was || b.date > was.date) latest.set(b.organisationId, b);
  }
  return [...latest.values()];
}

/**
 * A month's figure from what the system holds now. Balances keep their
 * history, so cash at month end is the month's own; forecasts, Xero's open
 * invoices and the registers are as they stand when the month is captured,
 * and the detail says so.
 */
export function pullMeasure(source: Exclude<MeasureSource, "entered">, month: string, model: FinanceModel, reg: RegistersModel, hub: HubMeasures | null = null): Pulled | null {
  const end = monthEnd(month);
  const asAt = reg.today < end ? reg.today : end;
  switch (source) {
    case "cash_month_end": {
      const b = model.balances.filter((x) => x.asAt <= asAt).sort((a, c) => c.asAt.localeCompare(a.asAt) || c.createdAt.localeCompare(a.createdAt))[0];
      return b ? { value: b.amount, detail: `Bank balance, ${dayText(b.asAt)}${b.source === "xero" ? " (Xero)" : ""}` } : null;
    }
    case "gate_low":
    case "runway": {
      const input = engineInput(model, "approved");
      if (!input) return null;
      const fc = forecast(input);
      if (source === "gate_low") return { value: fc.low.close, detail: `Week of ${dayText(fc.low.start)}, forecast on ${dayText(fc.asOf)}` };
      if (fc.firstOverdrawn) return { value: Math.round((daysBetween(fc.asOf, fc.firstOverdrawn) / 30.44) * 10) / 10, detail: `First week overdrawn ${dayText(fc.firstOverdrawn)}, forecast on ${dayText(fc.asOf)}` };
      return { value: Math.round(((model.settings.runOutWeeks * 7) / 30.44) * 10) / 10, detail: `No week overdrawn in the ${model.settings.runOutWeeks} weeks forecast: at least this` };
    }
    case "billed_unpaid": {
      const owed = model.synced.xeroDocs.filter((d) => d.kind === "invoice" && d.amountDue > 0 && d.date <= asAt);
      if (model.synced.connections.xero.mode === "off") return null;
      return { value: owed.reduce((s, d) => s + d.amountDue, 0), detail: `${owed.length} ${owed.length === 1 ? "invoice" : "invoices"} in Xero, as last read` };
    }
    case "mrr":
    case "customers":
    case "sites_billed": {
      if (model.synced.connections.hub.mode === "off") return null;
      const paying = payingAt(model, asAt);
      if (source === "customers") return { value: paying.length, detail: `Billed in the year to ${dayText(asAt)}, before VAT` };
      if (source === "sites_billed") return { value: paying.reduce((s, b) => s + b.sites, 0), detail: `${paying.length} customers` };
      return { value: Math.round(paying.reduce((s, b) => s + b.net / (b.schedule === "annual" ? 12 : 3), 0)), detail: `${paying.length} customers, before VAT` };
    }
    case "sites_live":
      return hub ? { value: hub.sitesLive, detail: `Customers through onboarding at ${dayText(end)}, less those gone` } : null;
    case "demo_conversion":
      return hub && hub.conversion.pct !== null ? { value: hub.conversion.pct, detail: `${hub.conversion.won} of ${hub.conversion.demoed} organisations demoed in ${monthName(month)} won since` } : null;
    case "retention_12m":
      return hub && hub.retention.pct !== null ? { value: hub.retention.pct, detail: `${hub.retention.retained} of ${hub.retention.base} customers at ${dayText(hub.retention.from)} still customers` } : null;
    case "open_actions":
    case "overdue_actions": {
      const open = reg.actions.filter((a) => a.status === "open");
      const n = source === "open_actions" ? open.length : open.filter((a) => actionState(a, dueOf(a, reg.milestones), reg.today, reg.settings.dueSoonDays) === "overdue").length;
      return { value: n, detail: `On ${dayText(reg.today)}` };
    }
    case "overdue_obligations": {
      const n = reg.obligations.filter((o) => o.active && obligationNow(o, { company: reg.company, yearStartMonth: reg.yearStartMonth }, reg.today).state === "overdue").length;
      return { value: n, detail: `On ${dayText(reg.today)}` };
    }
    case "high_risks": {
      const top = reg.riskScale.bands.length - 2;
      const n = reg.risks.filter((r) => r.status === "open" && bandOf(score(r.residual), reg.riskScale).index >= top).length;
      return { value: n, detail: `${reg.riskScale.bands.slice(-2).map((b) => b.name).join(" or ")}, on ${dayText(reg.today)}` };
    }
    case "changes_over_target": {
      const n = reg.releases.filter((r) => {
        if (r.status === "withdrawn") return false;
        const t = releaseTiming(r, reg.releaseFrameworks.find((f) => f.id === r.frameworkId), reg.today);
        return t.overDetect || t.overDeploy;
      }).length;
      return { value: n, detail: `On ${dayText(reg.today)}` };
    }
  }
}

export interface MeasureAt {
  value: MeasureValue | null;
  target: number | null;
  status: MeasureStatus | null;
  /** The month before's value, for the change. */
  previous: MeasureValue | null;
}

/** A measure's figure for a month, against its target and the month before. */
export function measureAt(d: Pick<MeasureDef, "id" | "better">, month: string, values: MeasureValue[], targets: MeasureTarget[], s: MeasureSettings): MeasureAt {
  const value = values.find((v) => v.measureId === d.id && v.month === month) ?? null;
  const target = targets.find((t) => t.measureId === d.id && t.month === month)?.value ?? null;
  const before = prevMonth(month);
  return { value, target, status: value ? statusOf(value.value, target, d.better, s) : null, previous: values.find((v) => v.measureId === d.id && v.month === before) ?? null };
}

/** The months to show: from the earliest live-from month or value to the current one. */
export function monthsToShow(defs: MeasureDef[], values: MeasureValue[], today: ISODate, max = 24): string[] {
  const now = monthOf(today);
  const starts = [...defs.map((d) => d.liveFrom).filter((x): x is string => !!x), ...values.map((v) => v.month)];
  const first = starts.length ? starts.sort()[0]! : now;
  const out: string[] = [];
  for (let m = first; m <= now && out.length < 240; m = nextMonth(m)) out.push(m);
  return out.slice(-max);
}

export interface SeriesPoint {
  month: string;
  /** Before the measure's live-from month: not charted. */
  live: boolean;
  value: number | null;
  target: number | null;
  status: MeasureStatus | null;
  confirmed: boolean;
  note: string;
}

/** A measure's months in order, for a trend: figure, target, status, whether confirmed, the note. */
export function measureSeries(d: Pick<MeasureDef, "id" | "better" | "liveFrom">, months: string[], values: MeasureValue[], targets: MeasureTarget[], s: MeasureSettings): SeriesPoint[] {
  return months.map((month) => {
    const live = isLive(d, month);
    const at = measureAt(d, month, values, targets, s);
    const v = live ? at.value : null;
    return { month, live, value: v?.value ?? null, target: live ? at.target : null, status: v ? at.status : null, confirmed: !!v?.confirmedAt, note: v?.note ?? "" };
  });
}

/** The months a trend covers: up to `count` (all when null), ending with the latest month that has a figure, or last month. */
export function trendMonths(defs: MeasureDef[], values: MeasureValue[], today: ISODate, count: number | null): string[] {
  const all = monthsToShow(defs, values, today, 240);
  const latest = [prevMonth(monthOf(today)), ...values.map((v) => v.month)].sort().at(-1)!;
  const upTo = all.filter((m) => m <= latest);
  return count === null ? upTo : upTo.slice(-count);
}
